"use client";

import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import Link from "next/link";
import { rashis } from "@ishvaram/domain-data";
import { trackFreeToolStarted, trackCalculatorUsed } from "@/lib/analytics-free-tools";
import { createEventId } from "@/lib/beacon-types";
import { safeLocalGet, safeLocalSet, safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";
import { edgeRead } from "@/lib/api/_fetch";
import { getTodayISTDateString } from "@/lib/ist-date";
import {
  BELOW_STICKY_CHROME_SCROLL_MARGIN,
  FLOATING_BOTTOM_CHROME_PX,
  belowStickyChromeClearancePx,
} from "@/lib/sticky-chrome-offsets";
import RashifalAudioPlayer from "@/components/RashifalAudioPlayer";
import type { RashiId } from "@ishvaram/domain-data/rashis";
import {
  assertEngineDailyHoroscope,
  formatEngineDate,
  type EngineDailyHoroscope,
  type EngineRashiPicture,
} from "@/components/horoscope/daily-engine.server";

import RashifalHubCheckSection from "./RashifalHubCheckSection";

/**
 * Daily gochar studio for /rashifal/ and /hi/aaj-ka-rashifal/.
 *
 * Every per-rashi value shown here is the engine's picture for the day
 * (`GET /rashifal/engine/daily`): Moon rashi, nakshatra, house from the rashi
 * and the governed transit cards. The studio holds no forecast, timing, colour
 * or house literal of its own — the labels below are
 * rashi names and glyphs only. When no engine day is available it says so and
 * shows nothing that could be mistaken for a reading.
 *
 * Below the studio, "Which rashi is yours?" (RashifalHubCheckSection) checks
 * the reader's birth details with the engine and opens their Moon sign here.
 */

const RASHI_GLYPHS: Readonly<Record<string, string>> = {
  mesh: "♈",
  vrishabh: "♉",
  mithun: "♊",
  kark: "♋",
  singh: "♌",
  kanya: "♍",
  tula: "♎",
  vrishchik: "♏",
  dhanu: "♐",
  makar: "♑",
  kumbh: "♒",
  meen: "♓",
};

/** @internal */
export const RASHI_SELECTOR = rashis.map((rashi) => ({
  id: rashi.id,
  name: rashi.name_en,
  hindi: rashi.name_hi,
  symbol: RASHI_GLYPHS[rashi.id] ?? "✦",
}));

/**
 * Split an engine picture into its lines: the lead sentence, then one fact per line.
 * @internal
 */
export function engineLines(content: string): string[] {
  return content
    .split(/\n{2,}/)
    .map((line) => line.trim())
    .filter(Boolean);
}

const NO_CHANGE = () => () => {};

/** The rashi this browser last chose on the studio (a per-viewer convenience). */
const RASHIFAL_RASHI_STORAGE_KEY = "ish_rashifal_rashi";
const RASHI_IDS: ReadonlySet<string> = new Set(RASHI_SELECTOR.map((rashi) => rashi.id));

function rememberedRashi(): string | null {
  const stored = safeLocalGet(RASHIFAL_RASHI_STORAGE_KEY);
  return stored && RASHI_IDS.has(stored) ? stored : null;
}

function bringReadingIntoView(heading: HTMLElement | null) {
  if (!heading) return;
  const { top, width, height } = heading.getBoundingClientRect();
  // No box (not laid out): there is nothing to bring into view.
  if (width === 0 && height === 0) return;
  // A heading under the sticky header (and the strip from sm) or under the
  // fixed bottom chrome is not really on screen, though the browser counts a
  // top >= 0 as inside the viewport. scrollIntoView then lands it at the H3's
  // scroll margin, which is the same clearance.
  const clearOfChrome =
    top >= belowStickyChromeClearancePx() && top <= window.innerHeight - FLOATING_BOTTOM_CHROME_PX;
  if (clearOfChrome) return;
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  heading.scrollIntoView({ block: "start", behavior: reduceMotion ? "auto" : "smooth" });
}

/** Brings a newly chosen reading into view; one the check opened also takes focus. */
function revealReading(heading: HTMLElement | null, focus: boolean) {
  bringReadingIntoView(heading);
  if (focus) heading?.focus({ preventScroll: true });
}

async function fetchTodayEngineHoroscope(
  language: "en" | "hi"
): Promise<EngineDailyHoroscope | null> {
  try {
    const raw = await edgeRead<unknown>(
      "rashifal/engine/daily",
      `language=${language}&day=today`,
      8_000
    );
    return assertEngineDailyHoroscope(raw);
  } catch {
    return null;
  }
}

export default function DailyGocharRashifalStudio({
  isHindi = false,
  initialSnapshot = null,
}: {
  isHindi?: boolean;
  /** The server's engine day, already checked against the hub's date stamp. */
  initialSnapshot?: EngineDailyHoroscope | null;
}) {
  // The server (and hydration) render the first rashi; after hydration the
  // visitor's remembered rashi takes over until they pick another.
  const [chosenRashiId, setChosenRashiId] = useState<string | null>(null);
  const remembered = useSyncExternalStore(NO_CHANGE, rememberedRashi, () => null);
  const selectedRashiId = chosenRashiId ?? remembered ?? RASHI_SELECTOR[0]?.id ?? "mesh";
  const readingHeading = useRef<HTMLHeadingElement | null>(null);
  const scrollOnSelect = useRef(false);
  const focusOnSelect = useRef(false);
  const [snapshot, setSnapshot] = useState<EngineDailyHoroscope | null>(initialSnapshot);
  // The viewer's IST civil day. The server value is the shipped engine day (or
  // the render's stamp), so hydration agrees with the HTML; after hydration the
  // client value is the viewer's own day and a prerendered hub that outlived
  // its day stops showing that day's picture as today's.
  const viewerToday = useSyncExternalStore(
    NO_CHANGE,
    getTodayISTDateString,
    () => initialSnapshot?.today ?? getTodayISTDateString()
  );
  const live = snapshot && snapshot.today === viewerToday ? snapshot : null;

  useEffect(() => {
    // Missing (rendered before the engine endpoint existed) or stale (ISR
    // outlived the day): fetch the viewer's day. A failed fetch leaves the
    // degraded state and does not retry on its own.
    if (live) return;
    let cancelled = false;
    void fetchTodayEngineHoroscope(isHindi ? "hi" : "en").then((fresh) => {
      if (!cancelled && fresh && fresh.today === viewerToday) setSnapshot(fresh);
    });
    return () => {
      cancelled = true;
    };
  }, [isHindi, live, viewerToday]);

  const rashi = useMemo(
    () => RASHI_SELECTOR.find((r) => r.id === selectedRashiId) ?? RASHI_SELECTOR[0],
    [selectedRashiId]
  );
  const picture = live?.rashis.find((r) => r.rashi === rashi.id) ?? null;

  const selection = useRef<{ id: string; attemptId: string; completed: boolean } | null>(null);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const handleSelect = (id: string) => {
    const attemptId = createEventId();
    selection.current = { id, attemptId, completed: false };
    trackFreeToolStarted({
      tool_name: "rashifal",
      attempt_id: attemptId,
      selected_rashi: id,
      period: "daily",
      language: isHindi ? "hi" : "en",
      source_path: window.location.pathname,
      step: "sign_selection",
    });
    safeLocalSet(RASHIFAL_RASHI_STORAGE_KEY, id);
    scrollOnSelect.current = true;
    setChosenRashiId(id);
    setSelectionVersion((version) => version + 1);
  };
  // The birth-details check below opens the reader's own sign. Its answer
  // already counted the check (calculator_used, rashi-calculator), so this is
  // not a sign tap: no free_tool_started here. The reading is above the check,
  // so it scrolls into view and takes focus, and the next Tab reads on from it.
  const openFromCheck = (id: RashiId) => {
    safeLocalSet(RASHIFAL_RASHI_STORAGE_KEY, id);
    scrollOnSelect.current = true;
    focusOnSelect.current = true;
    setChosenRashiId(id);
    setSelectionVersion((version) => version + 1);
  };
  useEffect(() => {
    // A tap changes the reading below the sign grid; on a phone that is often
    // under the fold or the floating chrome, so bring its heading into view.
    if (!scrollOnSelect.current) return;
    scrollOnSelect.current = false;
    revealReading(readingHeading.current, focusOnSelect.current);
    focusOnSelect.current = false;
  }, [selectionVersion]);
  useEffect(() => {
    const chosen = selection.current;
    if (!chosen || chosen.completed || !picture || picture.rashi !== chosen.id) return;
    chosen.completed = true;
    trackCalculatorUsed({
      calculator: "rashifal",
      attempt_id: chosen.attemptId,
      selected_rashi: chosen.id,
      period: "daily",
      language: isHindi ? "hi" : "en",
      source_path: window.location.pathname,
      step: "sign_selection",
    });
  }, [picture, isHindi, selectionVersion]);

  const handleConsult = () => {
    safeSessionSet(
      "ishvaram_intent_context",
      JSON.stringify({
        source: isHindi ? "/hi/aaj-ka-rashifal" : "/rashifal",
        rashi: rashi.name,
      })
    );
  };

  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/rashifal"),
    "/rashifal",
    isHindi ? "hi" : "en"
  );
  const shareText = encodeURIComponent(
    isHindi
      ? `${rashi.hindi} राशि का आज का वैदिक राशिफल, Ishvaram पर। https://ishvaram.com/hi/aaj-ka-rashifal/`
      : `Today's Vedic Rashifal for ${rashi.name} (${rashi.hindi}) on Ishvaram. https://ishvaram.com/rashifal/`
  );
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${shareText}`;

  return (
    <>
      <div
        className="my-8 rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-7 shadow-xs"
        data-engine-state={live ? "ready" : "unavailable"}
        data-engine-date={live?.date}
      >
        <StudioHeader isHindi={isHindi} />
        <RashiSelectorGrid selectedId={rashi.id} onSelect={handleSelect} isHindi={isHindi} />
        <RashiTransitCard
          rashi={rashi}
          picture={picture}
          snapshot={live}
          isHindi={isHindi}
          chatHref={chatHref}
          onConsult={handleConsult}
          whatsappShareUrl={whatsappShareUrl}
          headingRef={readingHeading}
        />
      </div>
      <RashifalHubCheckSection isHindi={isHindi} onOpenRashi={openFromCheck} />
    </>
  );
}

// The reading leads (C23, 2026-10-07): on a phone the share button and the
// chat CTA used to sit above it and pushed the reading ~1,400px down, so they
// now follow the reading in the card's action row.
function StudioHeader({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="pb-4 border-b border-border-default">
      <span className="text-caption font-bold uppercase tracking-wider text-intent-brand">
        {isHindi ? "दैनिक गोचर आधारित राशिफल" : "Daily Planetary Gochar Engine"}
      </span>
      <h2 className="text-subtitle sm:text-2xl font-bold font-serif text-text-primary mt-1">
        {isHindi
          ? "आज का वैदिक चंद्र राशिफल व गोचर विश्लेषण"
          : "Today's Vedic Moon-Sign Rashifal & Transit Studio"}
      </h2>
    </div>
  );
}

function RashiSelectorGrid({
  selectedId,
  onSelect,
  isHindi,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
  isHindi: boolean;
}) {
  const row = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // On a phone the signs are one scrollable row; keep the selected one (e.g.
    // a remembered Pisces) inside it. Scrolls the row only, never the page.
    const container = row.current;
    const chip = container?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!container || !chip || container.scrollWidth <= container.clientWidth) return;
    const left = chip.offsetLeft - container.offsetLeft;
    if (
      left < container.scrollLeft ||
      left + chip.offsetWidth > container.scrollLeft + container.clientWidth
    ) {
      container.scrollLeft = Math.max(0, left - 16);
    }
  }, [selectedId]);

  // One scrollable row on a phone (four rows of a 3-column grid pushed the
  // reading ~300px further down); the full grid from `sm`.
  return (
    <div
      ref={row}
      className="-mx-5 my-4 flex snap-x scroll-px-5 gap-2 overflow-x-auto px-5 pb-1 sm:mx-0 sm:my-6 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-6"
    >
      {RASHI_SELECTOR.map((item) => {
        const isSelected = item.id === selectedId;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            aria-pressed={isSelected}
            className={`inline-flex min-h-11 shrink-0 snap-start items-center justify-center gap-1.5 rounded-xl border px-3 py-1.5 text-center transition-all sm:flex-col sm:gap-0.5 sm:p-2.5 ${
              isSelected
                ? "border-intent-brand bg-surface-brand-subtle shadow-xs ring-2 ring-intent-brand/20 text-text-primary"
                : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
            }`}
          >
            <span className="text-lg sm:block" aria-hidden="true">
              {item.symbol}
            </span>
            <span className="text-body-small font-bold whitespace-nowrap sm:block">
              {isHindi ? item.hindi : item.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function EnginePicture({
  picture,
  snapshot,
  isHindi,
  rashiLabel,
}: {
  picture: EngineRashiPicture;
  snapshot: EngineDailyHoroscope;
  isHindi: boolean;
  rashiLabel: string;
}) {
  const [lead, ...facts] = engineLines(picture.content);
  return (
    <div className="mt-1">
      {lead && (
        <p className="mt-2 text-body leading-relaxed text-text-primary font-medium">{lead}</p>
      )}
      {facts.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-body text-text-secondary">
          {facts.map((line, index) => (
            <li key={index}>{line}</li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-caption text-text-muted" data-engine-dateline>
        {isHindi ? "इंजन गोचर चित्र:" : "Engine transit picture:"}{" "}
        {formatEngineDate(snapshot.date, isHindi ? "hi" : "en")}
      </p>
      {/* Provenance follows the reading it qualifies, so the reading itself
          starts right under the heading (C23). */}
      <p className="mt-0.5 text-caption text-text-secondary">
        {isHindi
          ? "चन्द्र राशि के अनुसार, Swiss Ephemeris ग्रह स्थितियों से"
          : "By Chandra rashi, from Swiss Ephemeris planetary positions"}
      </p>
      <div className="mt-3 pt-3 border-t border-border-default">
        <RashifalAudioPlayer
          textToSpeak={picture.content}
          rashiName={rashiLabel}
          isHindi={isHindi}
        />
      </div>
    </div>
  );
}

function EngineUnavailable({ isHindi }: { isHindi: boolean }) {
  return (
    <div className="mt-3">
      <p className="text-body-small leading-relaxed text-text-secondary">
        {isHindi
          ? "आज का इंजन गोचर चित्र अभी लोड नहीं हो सका, इसलिए यहाँ कोई फल नहीं दिखाया गया है। पहले पंचांग में चन्द्रमा की स्थिति देखें।"
          : "Today's engine transit picture could not be loaded, so no reading is shown here. Check the Moon's position in the Panchang first."}
      </p>
    </div>
  );
}

function RashiTransitCard({
  rashi,
  picture,
  snapshot,
  isHindi,
  chatHref,
  onConsult,
  whatsappShareUrl,
  headingRef,
}: {
  rashi: (typeof RASHI_SELECTOR)[number];
  picture: EngineRashiPicture | null;
  snapshot: EngineDailyHoroscope | null;
  isHindi: boolean;
  chatHref: string;
  onConsult: () => void;
  whatsappShareUrl: string;
  headingRef: RefObject<HTMLHeadingElement | null>;
}) {
  const rashiLabel = isHindi ? rashi.hindi : rashi.name;
  // One box around the reading (it used to be a card inside a card inside the
  // studio: ~205px of text width on a 360px phone).
  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <span className="text-3xl" aria-hidden="true">
          {rashi.symbol}
        </span>
        <div>
          <h3
            ref={headingRef}
            tabIndex={-1}
            className={`${BELOW_STICKY_CHROME_SCROLL_MARGIN} text-xl font-bold font-serif text-text-primary outline-none`}
          >
            {isHindi
              ? `${rashi.hindi} राशि — आज का गोचर फल`
              : `${rashi.name} (${rashi.hindi}) — Today's Transit`}
          </h3>
        </div>
      </div>

      {picture && snapshot ? (
        <EnginePicture
          picture={picture}
          snapshot={snapshot}
          isHindi={isHindi}
          rashiLabel={rashiLabel}
        />
      ) : (
        <EngineUnavailable isHindi={isHindi} />
      )}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <a
          href={chatHref}
          onClick={onConsult}
          className="inline-flex min-h-11 items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          {isHindi ? "व्यक्तिगत कुंडली से पूछें ✦" : "Analyze My Chart with AI ✦"}
        </a>
        <a
          href={whatsappShareUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-border-default bg-surface-raised px-3.5 py-2 text-caption font-bold text-text-primary hover:bg-surface-warm transition-colors"
        >
          {isHindi ? "WhatsApp पर शेयर करें" : "Share on WhatsApp"}
        </a>
      </div>

      {/* The card's sign link went with the 12 /rashifal/<sign>/ pages, which
          merged into the hubs on 2026-10-25; every sign's reading is on the
          hub below the studio. */}
      <div className="mt-4 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-end gap-3 text-caption">
        <Link
          href={isHindi ? "/hi/free-kundali/" : "/free-kundali/"}
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1 shrink-0"
        >
          {isHindi ? "मुफ्त जन्म कुंडली बनाएँ →" : "Calculate Full Birth Chart →"}
        </Link>
      </div>
    </div>
  );
}
